// src/api/apiService.js
import axios from "axios";

const API_URL = "https://d985-177-39-103-4.ngrok-free.app";

const api = axios.create({
  baseURL: API_URL,
});

// Interceptor para adicionar o token de autenticação automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para lidar com erros de resposta, como token expirado
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      alert("Sua sessão expirou. Por favor, faça login novamente.");
      localStorage.clear();
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Funções da API
export const fetchTipos = async (categoria, user) => {
  try {
    const url = user
      ? `/api/tipos/${categoria}?user=${user}`
      : `/api/tipos/${categoria}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar tipos:", error);
    throw new Error("Falha ao buscar tipos. Verifique o token.");
  }
};

export const fetchSolicitacoes = async (user) => {
  try {
    const endpoint = user
      ? `/api/solicitacoes?user=${user}` // Adiciona o parâmetro `user` se existir
      : "/api/solicitacoes";
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar solicitações:", error);
    return [];
  }
};

export const criarSolicitacao = async (tipoServicoId, file) => {
  try {
    const formData = new FormData();

    if (file) {
      formData.append("file", file); // Adiciona o arquivo ao FormData
    }

    const response = await api.post(
      `/api/solicitacoes/criar?tipoServicoId=${tipoServicoId}`, // tipoServicoId na query string
      formData, // Envia o arquivo no corpo
      {
        headers: {
          "Content-Type": "multipart/form-data", // Necessário para envio do arquivo
          Authorization: `Bearer ${localStorage.getItem("authToken")}`, // Token JWT no cabeçalho
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erro ao criar solicitação:",
      error.response || error.message
    );
    throw new Error("Falha ao criar solicitação.");
  }
};

export const criarSolicitacoes = async (tipoServicoIds, file, user) => {
  try {
    const formData = new FormData();
    if (file) {
      formData.append("file", file); // Adiciona o arquivo
    }
    tipoServicoIds.forEach((id) => formData.append("tipoServicoIds", id)); // Adiciona os IDs um a um

    // Monta a URL com o parâmetro user, se fornecido
    const url = user
      ? `/api/solicitacoes/criar/varias?user=${encodeURIComponent(user)}`
      : "/api/solicitacoes/criar/varias";

    const response = await api.post(url, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`, // Somente o token
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      "Erro ao criar solicitações:",
      error.response || error.message
    );
    throw new Error("Falha ao criar solicitações.");
  }
};

export const fetchSolicitacoesPorStatus = async (
  status,
  dataInicial,
  dataFinal,
  page = 0,
  size = 50,
  sort = "ascending" // Parâmetro de ordenação com valor padrão
) => {
  try {
    const response = await api.get(`/api/solicitacoes/status/${status}`, {
      params: { dataInicial, dataFinal, page, size, sort },
    });

    const { groups, totalPages, totalElements } = response.data;

    // Retornando apenas os dados agrupados e metadados de paginação
    return {
      groups: groups || {}, // Garante que sempre retorne um objeto, mesmo que vazio
      totalPages: totalPages || 0,
      totalElements: totalElements || 0,
    };
  } catch (err) {
    console.error("Erro ao buscar solicitações:", err);
    throw err;
  }
};


export const aceitarSolicitacao = async (id, observacao, dataConclusao) => {
  try {
    const response = await api.post(`/api/solicitacoes/${id}/aceitar`, null, {
      params: { observacao, dataConclusao },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao aceitar solicitação:", error);
    throw new Error("Falha ao aceitar solicitação.");
  }
};

export const negarSolicitacao = async (id, observacao) => {
  try {
    const response = await api.post(`/api/solicitacoes/${id}/negar`, null, {
      params: { observacao },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao negar solicitação:", error);
    throw new Error("Falha ao negar solicitação.");
  }
};

export const cadastrarServico = async (servico) => {
  try {
    const response = await api.post("/api/tipos", servico);
    return response.data;
  } catch (error) {
    console.error("Erro ao cadastrar serviço:", error);
    throw new Error("Falha ao cadastrar serviço.");
  }
};

export const fetchTiposPorCategoria = async (categoria, ativo = true) => {
  try {
    const response = await api.get(`/api/tipos/${categoria}`, {
      params: { ativo },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar tipos:", error);
    throw new Error("Falha ao buscar tipos.");
  }
};

export const atualizarStatusTipo = async (id) => {
  try {
    await api.patch(`/api/tipos/${id}/status`);
  } catch (error) {
    console.error("Erro ao atualizar status do tipo:", error);
    throw new Error("Falha ao atualizar status.");
  }
};

export const atualizarUsuario = async (dadosAtualizados) => {
  try {
    const response = await api.put("/api/usuario/me", dadosAtualizados);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const getUsuario = async () => {
  try {
    const response = await api.get("/api/usuario/me");
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar usuário:", error);
    throw new Error("Falha ao buscar usuário.");
  }
};

export const fetchEstatisticas = async (startDate, endDate, porConclusao) => {
  try {
    const response = await api.get("/api/estatisticas", {
      params: { start: startDate, end: endDate, porConclusao },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar estatísticas:", error);
    throw error;
  }
};

export const fetchLogs = async (startDate, endDate, isDownload = false) => {
  try {
    const endpoint = `/logs/download?startDate=${startDate} 00:00:00&endDate=${endDate} 23:59:59`;

    if (isDownload) {
      // Para download, a resposta será um Blob
      const response = await api.get(endpoint, { responseType: "blob" });
      return response.data; // Retorna o blob para o download
    } else {
      // Para exibição, tratamos como texto
      const response = await api.get(endpoint, { responseType: "text" });

      // Quebra a resposta por linhas e parseia cada linha como JSON
      const logs = response.data
        .trim()
        .split("\n")
        .map((line) => JSON.parse(line));

      return logs;
    }
  } catch (error) {
    console.error("Erro ao buscar os logs:", error);
    throw error;
  }
};

export const atualizarRequerImagem = async (id) => {
  try {
    await api.patch(`/api/tipos/${id}/requererImagem`);
  } catch (error) {
    console.error("Erro ao atualizar requerer imagem:", error);
    throw new Error("Falha ao atualizar o estado de requerer imagem.");
  }
};


// Função para validar o token
export const validateToken = async (token) => {
  try {
    const response = await api.get(`/api/upload/${token}/validate`);
    return response.data;
  } catch (error) {
    console.error("Erro ao validar o token:", error);
    throw new Error("Falha ao validar o token.");
  }
};

// Função para enviar documentos
export const uploadDocuments = async (token, tipos, files) => {
  try {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file); // Adiciona os arquivos ao FormData
    });

    const response = await api.post(`/api/upload/${token}?tipos=${tipos.join(",")}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Erro ao enviar documentos:", error);
    throw new Error("Falha ao enviar os documentos.");
  }
};

export const generateToken = async (solicitacoesETipos) => {
  try {
    const response = await api.post("/api/upload/generate-token", solicitacoesETipos);
    return response.data; // Retorna o token gerado
  } catch (error) {
    console.error("Erro ao gerar o token:", error);
    throw new Error("Falha ao gerar o token.");
  }
};

export const searchPaciente = async (cpf) => {
  try {
    const response = await api.get(`/api/pacientes/${cpf}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar paciente:", error);
    throw error;
  }
};

export const getUBSs = async (ativo = null) => {
  try {
    const headers = {};

    // 🔹 Sempre define o cabeçalho, mesmo quando ativo for false
    if (ativo !== null) {
      headers["X-Ativo"] = ativo.toString(); // Converte para string para evitar problemas
    }

    const response = await api.get("/api/ubs", { headers });
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar UBSs:", error);
    throw new Error("Falha ao buscar UBSs.");
  }
};


export const toggleUBSStatus = async (id) => {
  try {
    const response = await api.patch(`/api/ubs/${id}/status`);
    return response.data;
  } catch (error) {
    console.error("Erro ao alterar status da UBS:", error);
    throw new Error("Falha ao alterar status da UBS.");
  }
};

export const cadastrarUBS = async (ubsData) => {
  try {
    const response = await api.post("/api/ubs", ubsData);
    return response.data;
  } catch (error) {
    console.error("Erro ao cadastrar UBS:", error);
    throw new Error("Falha ao cadastrar UBS.");
  }
};

export const changePassword = async (passwordData) => {
  try {
    // Usando a instância 'api', o interceptor já irá adicionar o token se ele existir no localStorage.
    const response = await api.post("/auth/change-password", passwordData);
    return response.data;
  } catch (error) {
    throw new Error("Erro ao atualizar a senha. Tente novamente.");
  }
};

export const changeUserPassword = async (cpf, newPassword) => {
  try {
    const response = await api.put('/auth/change-user-password', { cpf, newPassword });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 406) {
      // Devolve a mensagem específica para o frontend
      return { error: "Não é possível atualizar a senha de usuários da mesma categoria." };
    }
    console.error("Erro ao alterar senha:", error);
    throw new Error("Erro ao alterar senha.");
  }
};

export const enviarPushSubscription = async (subscription) => {
  try {
    const response = await api.post("/api/subscribe", subscription);

    console.log("Assinatura enviada com sucesso:", response.data);
    return response.data;
  } catch (error) {
    console.error("Erro ao enviar a assinatura do Push Notification:", error);
    throw new Error("Falha ao enviar a assinatura do Push Notification.");
  }
};
